import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import back_app from '../../lib/back_app';
import platform from '../../lib/platform';
import { cat_id2lbl } from '../../lib/cat';
import BrandHeader from '../../components/brand/BrandHeader';

type Campaign = {
  id: string;
  name: string;
  description: string;
  category: string;
  status?: string;
  budget_info?: {
    creator_tiers?: Record<string, {enabled?: boolean, payment?: number}>;
    total_budget: number;
    spent: number;
    remaining: number;
  };
  targeting?: {
    countries?: string[];
  };
  allowed_ad_formats?: string[];
  is_active?: boolean;
  ts?: {insert: Date, modified: Date};
  active_creators_count?: number;
};

type Asset = {
  id: string;
  name: string;
  asset_type: string;
  file_url: string;
  mime_type: string;
  ts?: {insert: Date, modified: Date};
};

type Stats = {
  activeCampaigns: number;
  totalCampaigns: number;
  totalBudget: number;
  totalSpent: number;
  remainingBudget: number;
  totalAssets: number;
  activeCreators: number;
};

export default function BrandDashboard() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [stats, setStats] = useState<Stats>({
    activeCampaigns: 0,
    totalCampaigns: 0,
    totalBudget: 0,
    totalSpent: 0,
    remainingBudget: 0,
    totalAssets: 0,
    activeCreators: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const campaignsResult = await back_app.campaign_list();
      const assetsResult = await back_app.basset_list();

      if (!campaignsResult.err) {
        const campaignsData = Array.isArray(campaignsResult) ? campaignsResult : [];
        
        
        const campaignsWithCreators = await Promise.all(
          campaignsData.map(async (campaign: Campaign) => {
            try {
              const creatorsResult = await back_app.vcamp_list({campaign_id: campaign.id});
              return {
                ...campaign,
                active_creators_count: !creatorsResult.err && Array.isArray(creatorsResult) ? creatorsResult.length : 0
              };
            } catch (error) {
              return { ...campaign, active_creators_count: 0 };
            }
          })
        );

        setCampaigns(campaignsWithCreators);

        const totalBudget = campaignsWithCreators.reduce(
          (sum: number, c: Campaign) => sum + (c.budget_info?.total_budget || 0), 0
        );
        const totalSpent = campaignsWithCreators.reduce(
          (sum: number, c: Campaign) => sum + (c.budget_info?.spent || 0), 0
        );
        const remainingBudget = campaignsWithCreators.reduce(
          (sum: number, c: Campaign) => sum + (c.budget_info?.remaining || 0), 0
        );
        const activeCampaigns = campaignsWithCreators.filter((c: Campaign) => c.status == 'active').length;
        const activeCreators = campaignsWithCreators.reduce(
          (sum: number, c: Campaign) => sum + (c.active_creators_count || 0), 0
        );

        const assetsData = !assetsResult.err && Array.isArray(assetsResult) ? assetsResult : [];
        const validAssets = assetsData.filter(
          (a: Asset) => a.asset_type === 'video_ad' || a.asset_type === 'banner'
        );
        setAssets(validAssets);

        setStats({
          activeCampaigns,
          totalCampaigns: campaignsWithCreators.length,
          totalBudget,
          totalSpent,
          remainingBudget,
          totalAssets: validAssets.length,
          activeCreators,
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if ((error as Error)?.message == 'no session') navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <BrandHeader />

      {}
      <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2">Brand Dashboard</h2>

            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/brand/upload-asset')}
                className="px-6 py-3 bg-white/10 backdrop-blur border-2 border-white/30 text-white rounded-lg font-semibold hover:bg-white/20 transition-all text-center"
              >
                Upload Asset
              </button>
              <button
                onClick={() => navigate('/brand/create-campaign')}
                className="px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:shadow-lg transition-all text-center"
              >
                + Create Campaign
              </button>
            </div>
          </div>
        </div>
      </div>
                  <p className="text-white/90">Manage your campaigns and assets</p>
      {}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-6 sm:-mt-8 mb-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"/>
                </svg>
              </div>
            </div>
            <div className="text-gray-600 text-xs sm:text-sm mb-1">Active Campaigns</div>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900">
              {stats.activeCampaigns}
            </div>
            <div className="text-xs sm:text-sm text-gray-500 mt-1">
              {stats.totalCampaigns} total
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                </svg>
              </div>
            </div>
            <div className="text-gray-600 text-xs sm:text-sm mb-1">Total Budget</div>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900">
              ${stats.totalBudget.toLocaleString()}
            </div>
            <div className="text-xs sm:text-sm text-green-600 mt-1">
              ${stats.remainingBudget.toLocaleString()} left
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                </svg>
              </div>
            </div>
            <div className="text-gray-600 text-xs sm:text-sm mb-1">Active Creators</div>
            <div className="text-2xl sm:text-3xl font-bold text-purple-600">
              {stats.activeCreators}
            </div>
            <div className="text-xs sm:text-sm text-gray-500 mt-1">
              Participating
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"/>
                </svg>
              </div>
            </div>
            <div className="text-gray-600 text-xs sm:text-sm mb-1">Total Assets</div>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900">
              {stats.totalAssets}
            </div>
            <div className="text-xs sm:text-sm text-gray-500 mt-1">
              Videos & Banners
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 space-y-8">
        {}
        <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Recent Campaigns</h2>
            <button
              onClick={() => navigate('/brand/campaigns')}
              className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center gap-1"
            >
              <span>View All</span>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"/>
              </svg>
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-600 mt-4">Loading...</p>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📋</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No campaigns yet</h3>
              <p className="text-gray-600 mb-6">Create your first campaign to start reaching creators</p>
              <button
                onClick={() => navigate('/brand/create-campaign')}
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                Create First Campaign
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {campaigns.slice(0, 6).map((campaign) => {
                const spent = campaign.budget_info?.spent || 0;
                const totalBudget = campaign.budget_info?.total_budget || 0;
                const remaining = campaign.budget_info?.remaining || 0;
                const spentPercentage = totalBudget > 0 ? (spent / totalBudget) * 100 : 0;

                return (
                  <div
                    key={campaign.id}
                    className="bg-white border shadow-sm rounded-xl p-5 hover:shadow-lg transition-all cursor-pointer group"
                    onClick={() => campaign.status === 'draft'
                      ? navigate(`/brand/create-campaign/${campaign.id}`)
                      : navigate(`/brand/campaigns/${campaign.id}`)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-bold text-gray-900 text-lg flex-1 line-clamp-2 group-hover:text-blue-600 transition-colors">
                        {campaign.name || 'Untitled Draft'}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ml-2 flex-shrink-0 ${
                        campaign.status === 'draft' ? 'bg-yellow-400 text-yellow-900'
                        : campaign.status == 'active' ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'
                      }`}>
                        {campaign.status === 'draft' ? 'Draft' : campaign.status == 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    {campaign.category && (
                      <div className="mb-3">
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                          {cat_id2lbl(campaign.category)}
                        </span>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="bg-blue-50 rounded-lg p-3">
                        <div className="text-xs text-gray-600 mb-1">CPS</div>
                        <div className="text-lg font-bold text-blue-600">
                          {platform.cps_lbl(campaign.budget_info?.creator_tiers)}
                        </div>
                      </div>

                      <div className="bg-purple-50 rounded-lg p-3">
                        <div className="text-xs text-gray-600 mb-1">Creators</div>
                        <div className="text-lg font-bold text-purple-600">
                          {campaign.active_creators_count || 0}
                        </div>
                      </div>
                    </div>

                    {totalBudget > 0 && (
                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-gray-600">Budget</span>
                          <span className="font-bold text-gray-900">${totalBudget.toLocaleString()}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                          <div
                            className="bg-gradient-to-r from-blue-500 to-purple-500 h-2.5 rounded-full transition-all"
                            style={{ width: `${Math.min(spentPercentage, 100)}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-500">Spent: ${spent.toLocaleString()}</span>
                          <span className="text-green-600 font-semibold">Left: ${remaining.toLocaleString()}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {}
        {assets.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Recent Assets</h2>
              <button
                onClick={() => navigate('/brand/assets')}
                className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center gap-1"
              >
                <span>View All</span>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"/>
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {assets.slice(0, 6).map((asset) => (
                <div
                  key={asset.id}
                  onClick={() => navigate('/brand/assets')}
                  className="bg-white border rounded-xl overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                >
                  <div className="aspect-video bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center relative overflow-hidden">
                    {asset.mime_type?.startsWith('video/') ? (
                      <video src={asset.file_url} className="w-full h-full object-cover" muted />
                    ) : (
                      <img src={asset.file_url} alt={asset.name} className="w-full h-full object-cover" />
                    )}
                    <div className="absolute top-2 right-2">
                      <span className="px-2 py-1 bg-blue-500 text-white text-xs font-semibold rounded">
                        {asset.asset_type === 'video_ad' ? '📺' : '🖼️'}
                      </span>
                    </div>
                  </div>
                  <div className="p-2">
                    <p className="text-xs font-semibold text-gray-900 truncate">{asset.name}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div
            onClick={() => navigate('/brand/create-campaign')}
            className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer group"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd"/>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 mb-1">Create New Campaign</h3>
                <p className="text-sm text-gray-600">Launch a new campaign to reach creators</p>
              </div>
            </div>
          </div>

          <div
            onClick={() => navigate('/brand/upload-asset')}
            className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer group"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M5.5 13a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 13H11V9.413l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13H5.5z"/>
                  <path d="M9 13h2v5a1 1 0 11-2 0v-5z"/>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 mb-1">Upload New Asset</h3>
                <p className="text-sm text-gray-600">Add videos or banners to your library</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}